<template>
  <div>
    <div class="flex">
      <div class="flex w-12 flex-col">
        <img
          class="h-12 w-full rounded-full border-2 bg-stone-200 object-contain"
          :src="`/images/reviews/${review.storeReviewPlatform.platform.slug}.svg`"
          :alt="review.storeReviewPlatform.platform.slug"
        />
        <div class="flex w-12 shrink-0 grow self-stretch py-1 text-left">
          <div v-if="isReplyState" class="z-0 mx-auto mt-0 w-0.5 bg-stone-200 p-0" />
        </div>
      </div>

      <div class="ml-4 flex w-full flex-col">
        <div class="flex justify-between">
          <div>
            <div class="flex flex-col items-start pb-2 md:flex-row md:items-center md:pb-0">
              <span class="text-sm font-bold text-foreground sm:text-base">{{ review.name }}</span>
              <div class="ml-0 md:ml-2">
                <ReviewRating v-if="review.rating" :rating="review.rating" />
                <ReviewRecommended v-else :recommended="review.hasRecommended" />
              </div>
            </div>
            <div class="mt-1 text-xs font-medium text-foreground-secondary md:flex md:items-center">
              <div class="flex items-center">
                <BaseIcon name="HeroClockIconOutline" class="h-4 w-4" />
                <div class="mx-2">
                  {{
                    $t('pages.review.reviewPage.items.date', {
                      date: new Date(review.date).toLocaleDateString('fr-FR'),
                      time: new Date(review.date).toLocaleTimeString('fr-FR')
                    })
                  }}
                </div>
              </div>
              <div class="mt-1 flex items-center md:mx-4 md:mt-0">
                <BaseIcon name="HeroMapPinIconOutline" class="h-4 w-4" />
                <div class="mx-2">
                  {{ review.store.name }}
                </div>
              </div>
            </div>
          </div>
          <Button v-if="showReplyLink" variant="link" size="sm" as-child class="px-0 text-primary">
            <a :href="review.sourceUrl" target="_blank">
              {{ $t('pages.review.reviewPage.items.buttons.reply') }}
              <SquareArrowOutUpRight />
            </a>
          </Button>
          <ReviewRepliedBadge v-else :has-replied="review.ownerResponseText?.length > 1" />
        </div>
        <div
          class="mt-4 pb-5 text-sm"
          :class="review.text ? 'text-foreground-secondary' : 'text-muted-foreground italic'"
        >
          <p>{{ review.text ? review.text : $t('pages.review.reviewPage.items.noReview') }}</p>
        </div>
      </div>
    </div>
    <Button
      v-if="!displayReplyForm && !review.ownerResponseText && !showReplyLink"
      variant="default-primary"
      class="ml-14"
      @click="displayReplyForm = !displayReplyForm"
    >
      <Undo2 />
      {{ $t('pages.review.reviewPage.items.buttons.reply') }}
    </Button>
    <div v-if="isReplyState" class="flex">
      <div class="flex h-12 w-12 flex-col">
        <img class="h-full w-full rounded-full border-2 bg-stone-200 object-contain" :src="merchantLogo" alt="" />
      </div>
      <div class="ml-4 flex flex-1 flex-col">
        <div class="flex items-center justify-between">
          <div class="text-sm font-bold text-foreground sm:text-base">
            {{ review.store.name }} <span v-if="user.jobTitle">({{ user.jobTitle }})</span>
          </div>
        </div>
        <template v-if="!displayReplyForm">
          <div class="mt-2 text-sm italic text-foreground-secondary">
            {{ transformOwnerResponse(review.ownerResponseText, review) }}
          </div>
          <div
            v-if="!showReplyLink"
            class="mt-2 flex flex-col items-center space-x-0 sm:flex-row sm:space-x-2 md:flex-row"
          >
            <Button variant="default-primary" class="mt-2 w-full md:w-auto" @click="displayReplyForm = true">
              {{ $t('pages.review.reviewPage.items.buttons.modify') }}
            </Button>
            <BaseConfirmButton
              class="mt-2 w-full md:w-auto"
              :outlined="true"
              :processing="isProcessing"
              :title="$t('pages.review.reviewPage.items.confirm.title')"
              :text="$t('pages.review.reviewPage.items.confirm.text')"
              :cancel-button-label="$t('pages.review.reviewPage.items.confirm.cancel')"
              :confirm-button-label="$t('pages.review.reviewPage.items.confirm.confirm')"
              @confirm="deleteResponse"
            >
              {{ $t('pages.review.reviewPage.items.buttons.delete') }}
            </BaseConfirmButton>
          </div>
        </template>
        <ReviewReplyForm v-else :review="review" @reply="reply" @cancel="displayReplyForm = false" />
      </div>
    </div>
  </div>
</template>
<script>
import { SquareArrowOutUpRight, Undo2 } from 'lucide-vue-next'

import ReviewReplyForm from './ReviewReplyForm.vue'
import ReviewRating from './ReviewRating.vue'
import ReviewRepliedBadge from './ReviewRepliedBadge.vue'
import { BaseConfirmButton } from '@/ui/alert-dialog'
import { Button } from '@/ui/button'
import useReview from '@/composables/review'
import ReviewRecommended from './ReviewRecommended.vue'
import useLocalStorage from '@/composables/useLocalStorage'

export default {
  components: {
    ReviewReplyForm,
    ReviewRating,
    ReviewRepliedBadge,
    BaseConfirmButton,
    Button,
    ReviewRecommended,
    SquareArrowOutUpRight,
    Undo2
  },
  props: {
    review: {
      type: Object,
      required: true
    }
  },
  emits: ['reply', 'deleteResponse'],
  setup() {
    const { merchantLogo, user, permissions } = useLocalStorage()
    const notification = useNotification()
    const { transformOwnerResponse } = useReview()

    return {
      merchantLogo,
      user,
      permissions,
      notification,
      transformOwnerResponse
    }
  },
  data: () => {
    return {
      displayReplyForm: false,
      isProcessing: false
    }
  },
  computed: {
    isReplyState() {
      return this.review.ownerResponseText || this.displayReplyForm
    },
    showReplyLink() {
      if (!this.permissions['review.read']) {
        return true
      }

      return !this.review.reviewManager.hasReplyFeature
    }
  },
  methods: {
    reply(responseText) {
      this.displayReplyForm = false
      this.$emit('reply', responseText)
    },
    deleteResponse() {
      this.isProcessing = true
      this.axios
        .patch(`/reviews/${this.review.id}`, {
          response: this.response
        })
        .then(() => {
          this.notification.create('success', {
            text: this.$t('pages.review.reviewReplyForm.success.deletedReply')
          })
          this.$emit('deleteResponse')
        })
        .catch(() => {
          this.notification.create('error', {
            text: this.$t('pages.review.reviewReplyForm.error.deletedReply'),
            persist: true
          })
        })
        .finally(() => {
          this.isProcessing = false
        })
    }
  }
}
</script>
